import { Injectable } from '@angular/core';
import { Observable, from } from 'rxjs';
import { switchMap } from 'rxjs/operators';
import { OrderService } from 'src/app/services/pedidos/pedidos.service';
import { SetValuesFileAttachment } from 'src/app/services/pedidos/gateway-pedidos.mapper';
import { RejectReason } from 'src/app/shared/models/RejectReason';
import { ReturnReason } from 'src/app/shared/models/returnReason';
import {
  SeguimientoAnonimoViajeRequest,
  SeguimientoAnonimoViajeResponse,
  SeguimientoAnonimoViajesGatewayApi,
} from 'src/core/apis/seguimiento-anonimo.viajes';
import { HormigonDispatchExtraValues, HormigonDispatchOrder, HormigonDispatchSignature, DispatchMutationResult } from './hormigon-dispatch.types';

/**
 * Fachada del despacho Hormigón Mixer.
 *
 * En esta primera extracción delega en OrderService para conservar los
 * endpoints, payloads y comportamiento offline existentes. Los componentes
 * Hormigón dependen de esta fachada y no del servicio legado.
 */
@Injectable({ providedIn: 'root' })
export class HormigonDispatchFacade {
  constructor(
    private readonly orderService: OrderService,
    private readonly anonymousTripsGateway: SeguimientoAnonimoViajesGatewayApi,
  ) {}

  list(forceNetwork = false): Observable<HormigonDispatchOrder[]> {
    return from(this.orderService.getAllOrders(forceNetwork)).pipe(
      switchMap((orders$) => orders$)
    );
  }

  getByTicket(
    tkt: string,
    background = false,
    forceNetwork = false,
  ): Observable<HormigonDispatchOrder[]> {
    return this.orderService.getOrder(tkt, background, forceNetwork);
  }

  updateStatus(
    tkt: string,
    orderNumber: string,
    status: number,
    hour?: string
  ): Observable<unknown> {
    return this.orderService.updateStatus(tkt, orderNumber, status, hour);
  }

  saveExtraValues(
    tkt: string,
    orderNumber: string,
    values: HormigonDispatchExtraValues
  ): Observable<DispatchMutationResult> {
    return this.orderService.setExtraValues(
      tkt,
      orderNumber,
      values.waterAddition,
      values.anotherAddition,
      values.guidingRemarks,
      values.m3Returned,
      values.reasonToReturn,
      values.m3ReturnedPallet,
      values.files,
      values.queuedFiles ?? []
    );
  }

  saveWaterAddition(
    tkt: string,
    orderNumber: string,
    value: boolean
  ): Observable<DispatchMutationResult> {
    return this.orderService.setWaterAddition(tkt, orderNumber, value);
  }

  saveAnotherAddition(
    tkt: string,
    orderNumber: string,
    value: boolean
  ): Observable<DispatchMutationResult> {
    return this.orderService.setAnotherAddition(tkt, orderNumber, value);
  }

  saveGuidingRemarks(
    tkt: string,
    orderNumber: string,
    remarks: string
  ): Observable<DispatchMutationResult> {
    return this.orderService.setGuidingRemarks(tkt, orderNumber, remarks, true);
  }

  saveM3Returned(
    tkt: string,
    orderNumber: string,
    m3: number
  ): Observable<DispatchMutationResult> {
    return this.orderService.setM3Returned(tkt, orderNumber, m3);
  }

  reject(
    tkt: string,
    orderNumber: string,
    rejectReason: string,
    rejectDescription: string,
    files: File[],
    rutClient = '',
    queuedFiles: SetValuesFileAttachment[] = []
  ): Observable<DispatchMutationResult> {
    return this.orderService.setRejectValues(
      tkt,
      orderNumber,
      rejectReason,
      rejectDescription,
      files,
      true,
      rutClient,
      queuedFiles
    );
  }

  saveUnsignedGuide(tkt: string, orderNumber: string): Observable<unknown> {
    return this.orderService.setUnsignedGuide(tkt, orderNumber);
  }

  savePaperSignature(
    tkt: string,
    orderNumber: string,
    signature: HormigonDispatchSignature
  ): Observable<DispatchMutationResult> {
    return this.orderService.setSignedGuide(
      tkt,
      orderNumber,
      signature.rut,
      signature.name,
      signature.signature,
      signature.email
    );
  }

  saveCanvasSignature(
    tkt: string,
    orderNumber: string,
    signature: HormigonDispatchSignature,
    alreadySigned = false
  ): Observable<DispatchMutationResult> {
    const isRecepcion = !signature.tipoFirma || signature.tipoFirma === 'RECEPCION';
    return this.orderService.setSignedGuideCanvas(
      tkt,
      orderNumber,
      signature.rut,
      signature.name,
      signature.signature,
      signature.email,
      alreadySigned && isRecepcion,
      signature.tipoFirma,
      signature.comentario
    );
  }

  getRejectReasons(): Observable<RejectReason[]> {
    return this.orderService.getRejectReasons();
  }

  getReturnReasons(): Observable<ReturnReason[]> {
    return this.orderService.getReturnReasons();
  }

  changeDeliveryPoint(
    tkt: string,
    orderNumber: string,
    lat: number,
    lng: number
  ): Observable<unknown> {
    return this.orderService.changeDeliveryPoint(tkt, orderNumber, lat, lng);
  }

  setPendingOrder(tkt: string, orderNumber: string): Observable<unknown> {
    return this.orderService.setPendingOrder(tkt, orderNumber);
  }

  clearPendingOrder(
    tkt: string,
    orderNumber: string,
    options?: { commentsFinal?: string; finFlujo?: boolean }
  ): Observable<unknown> {
    return this.orderService.clearPendingOrder(tkt, orderNumber, options);
  }

  regenerateQr(tkt: string, orderNumber: string): Observable<unknown> {
    return this.orderService.setRegenerateQr(tkt, orderNumber);
  }

  getAnonymousSignature(
    request: SeguimientoAnonimoViajeRequest
  ): Observable<SeguimientoAnonimoViajeResponse> {
    return this.anonymousTripsGateway.post(request);
  }
}
